/**
 * Générateur — Concours des médiatrices d'un triangle
 * Attendu ID : 670 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - propriete_concours_qcm              : QCM 4 choix (énoncé de la propriété)
 *   - raisonnement_concours_ordonner      : 4 étapes à ordonner (preuve du concours)
 *   - deduction_distances_qcm             : QCM 4 choix (application OA=OB=OC)
 *   - caracterisation_point_vf            : V/F 3V + 3F (discrimine droites remarquables)
 *   - raisonnement_texte_trou             : texte à trous (5 trous, banque de mots)
 *   - association_hypothese_conclusion_relier : relier hypothèse ↔ conclusion (4 paires)
 *
 * Spécificité : 100 % texte pur (pas de SVG) pour maximiser la stabilité
 * du fingerprint audit. Aucun exercice ne qualifie O autrement que comme
 * « point de concours des médiatrices » — la qualification "centre du cercle
 * circonscrit" est réservée à l'attendu 671.
 *
 * Partition avec attendus voisins :
 *   - 660 : propriété caractéristique de la médiatrice (prérequis, mobilisé ici).
 *   - 670 : concours des trois médiatrices (ce générateur).
 *   - 671 : cercle circonscrit (suite logique, autre attendu).
 *
 * Variantes estimées : plusieurs dizaines de milliers par format (objectif :
 * 20/20 uniques sur 20 itérations auditGen, stable sur 5 runs).
 */

import {
    randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeVraiFaux, makeRelier, makeOrdonner, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 670;
// On exclut I, O (risque de confusion avec 1, 0) ET on réserve O pour désigner
// le point de concours. Pool effectif : 22 lettres.
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('').filter(c => c !== 'O');

const L_PROP   = 'Propriété du concours des médiatrices (QCM)';
const L_ORDRE  = 'Raisonnement du concours (ordering)';
const L_DED    = 'Déduction des égalités de distances (QCM)';
const L_VF     = 'Caractérisation du point de concours (V/F)';
const L_TROU   = 'Complétion du raisonnement (texte à trous)';
const L_RELIER = 'Association hypothèse ↔ conclusion (relier)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_PROP,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

// Format français pour les décimaux (virgule, supprime la décimale si entière).
const fr = (x) => {
    if (Number.isInteger(x)) return String(x);
    return String(x).replace('.', ',');
};

// ─── Format 1 — Propriété du concours (QCM) ──────────────────

const genProprieteConcours = () => {
    const [L1, L2, L3] = pickLetters(3);
    const r = Math.random();

    let choix, bonne, texte;

    if (r < 0.50) {
        // V1 — formulation canonique
        bonne = 'Elles sont toujours concourantes.';
        choix = [
            bonne,
            'Elles sont toujours parallèles deux à deux.',
            'Elles forment toujours un triangle plus petit à l\'intérieur du triangle.',
            'Elles sont concourantes uniquement si le triangle est équilatéral.',
        ];
        texte = `<p>Dans un triangle ${L1}${L2}${L3}, que peut-on dire des trois médiatrices de ses côtés ?</p>`;
    } else if (r < 0.80) {
        // V2 — oui/non reformulé
        bonne = 'Oui, pour tout triangle.';
        choix = [
            bonne,
            'Non, jamais.',
            'Seulement si le triangle est isocèle.',
            'Seulement si le triangle est rectangle.',
        ];
        texte = `<p>Soit un triangle ${L1}${L2}${L3}. Les médiatrices de [${L1}${L2}], de [${L2}${L3}] et de [${L1}${L3}] se coupent-elles en un même point ?</p>`;
    } else {
        // V3 — nombre de points d'intersection
        bonne = '1';
        choix = ['1', '2', '3', '0'];
        texte = `<p>Combien de points d'intersection distincts obtient-on en traçant les trois médiatrices d'un triangle ${L1}${L2}${L3} ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_PROP));
    payload._meta.generatorKey = 'propriete_concours_qcm';
    return payload;
};

// ─── Format 2 — Raisonnement ordonné (4 étapes) ──────────────

// Les 3 arrangements possibles de 2 côtés cités dans l'hypothèse.
// Chaque entrée : { cites: [[X1,X2], [Y1,Y2]], reste: [Z1,Z2] }
// où [X1X2] et [Y1Y2] sont les médiatrices citées dans l'hypothèse,
// et [Z1Z2] est la 3e (déduite par le raisonnement).
const _arrangementsCotes = (L1, L2, L3) => [
    { cites: [[L1, L2], [L2, L3]], reste: [L1, L3] },
    { cites: [[L1, L2], [L1, L3]], reste: [L2, L3] },
    { cites: [[L2, L3], [L1, L3]], reste: [L1, L2] },
];

const genRaisonnementOrdonner = () => {
    const [L1, L2, L3] = pickLetters(3);
    const arrangements = _arrangementsCotes(L1, L2, L3);
    const { cites, reste } = randomChoice(arrangements);
    const [X1, X2] = cites[0];
    const [Y1, Y2] = cites[1];
    const [Z1, Z2] = reste;

    const items = [
        `Comme O appartient aux médiatrices de [${X1}${X2}] et [${Y1}${Y2}], on a O${X1} = O${X2} et O${Y1} = O${Y2}.`,
        `Des deux égalités précédentes, on déduit que O${Z1} = O${Z2}.`,
        `Puisque O${Z1} = O${Z2}, le point O appartient aussi à la médiatrice de [${Z1}${Z2}] : les trois médiatrices passent donc par O.`,
    ];

    const texte = `<p>Voici le raisonnement qui permet de prouver que les trois médiatrices d'un triangle ${L1}${L2}${L3} sont concourantes. On suppose que O est le point d'intersection des médiatrices de [${X1}${X2}] et [${Y1}${Y2}]. Remets les étapes suivantes dans le bon ordre.</p>`;

    const payload = makeOrdonner(items, texte, opts(L_ORDRE));
    payload._meta.generatorKey = 'raisonnement_concours_ordonner';
    return payload;
};

// ─── Format 3 — Déduction des distances (QCM) ────────────────

const DISTANCES_POOL = [2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8];

const genDeductionDistances = () => {
    const [L1, L2, L3] = pickLetters(3);
    const r = randomChoice(DISTANCES_POOL);

    let choix, bonne, texte;

    if (Math.random() < 0.5) {
        // V1 — sens direct : du concours vers l'égalité des distances.
        // Toutes les options chiffrées portent la MÊME valeur que l'énoncé :
        // le nombre ne désigne donc plus la bonne réponse. Les distracteurs
        // reprennent les trois confusions réelles du niveau : équidistance aux
        // côtés (bissectrices), distance au sommet lue comme longueur de côté,
        // propriété crue réservée à un triangle particulier.
        bonne = `O${L2} = O${L3} = ${fr(r)} cm.`;
        choix = [
            bonne,
            `La distance de O à chaque côté du triangle vaut ${fr(r)} cm.`,
            `${L1}${L2} = ${L1}${L3} = ${fr(r)} cm.`,
            'On ne peut rien en déduire sans connaître la nature du triangle.',
        ];
        texte = `<p>O est le point de concours des médiatrices d'un triangle ${L1}${L2}${L3}. On sait que O${L1} = ${fr(r)} cm. Que peut-on en déduire ?</p>`;
    } else {
        // V2 — sens réciproque : de l'égalité des distances vers le concours.
        // Aucune option ne porte de nombre. Le distracteur « bissectrices » est
        // volontairement le jumeau de la bonne réponse : c'est exactement la
        // discrimination visée.
        bonne = 'O est le point de concours des trois médiatrices du triangle.';
        choix = [
            bonne,
            'O est le point de concours des trois bissectrices du triangle.',
            `O est le milieu du côté [${L1}${L2}].`,
            'O est nécessairement situé à l\'intérieur du triangle.',
        ];
        texte = `<p>Dans un triangle ${L1}${L2}${L3}, un point O vérifie O${L1} = O${L2} = O${L3} = ${fr(r)} cm. Que peut-on en déduire ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_DED));
    payload._meta.generatorKey = 'deduction_distances_qcm';
    return payload;
};

// ─── Format 4 — Caractérisation (V/F) ────────────────────────

const buildAffirmationsVF = () => {
    const [L1, L2, L3] = pickLetters(3);
    return {
        VRAIS: {
            V1: () => ({
                id: 'V1', verite: 0,
                texte: 'Les trois médiatrices d\'un triangle sont toujours concourantes.',
            }),
            V2: () => ({
                id: 'V2', verite: 0,
                texte: `Si O est le point de concours des médiatrices du triangle ${L1}${L2}${L3}, alors O${L1} = O${L2} = O${L3}.`,
            }),
            V3: () => ({
                id: 'V3', verite: 0,
                texte: 'Dans un triangle, la médiatrice d\'un côté passe par le milieu de ce côté.',
            }),
            V4: () => ({
                id: 'V4', verite: 0,
                texte: 'Le raisonnement qui prouve le concours des médiatrices utilise la propriété : tout point de la médiatrice d\'un segment est équidistant des extrémités de ce segment.',
            }),
            V5: () => ({
                id: 'V5', verite: 0,
                texte: 'Un triangle possède trois médiatrices (une par côté).',
            }),
        },
        FAUX: {
            F1: () => ({
                id: 'F1', verite: 1,
                texte: 'Les trois médiatrices d\'un triangle sont parallèles deux à deux.',
            }),
            F2: () => ({
                id: 'F2', verite: 1,
                texte: 'Les trois médiatrices d\'un triangle ne sont concourantes que si le triangle est équilatéral.',
            }),
            F3: () => ({
                id: 'F3', verite: 1,
                texte: `Dans un triangle ${L1}${L2}${L3}, les médiatrices sont les droites qui partagent chaque angle en deux angles égaux.`,
            }),
            F4: () => ({
                id: 'F4', verite: 1,
                texte: 'Le point de concours des médiatrices d\'un triangle est toujours situé à l\'intérieur du triangle.',
            }),
            F5: () => ({
                id: 'F5', verite: 1,
                texte: 'Les trois médiatrices d\'un triangle se coupent seulement en deux points distincts.',
            }),
        },
    };
};

const genCaracterisationVF = () => {
    let tries = 0;
    while (tries < 5) {
        const builders = buildAffirmationsVF();
        const idsV = randomChoiceMultiple(Object.keys(builders.VRAIS), 3);
        const idsF = randomChoiceMultiple(Object.keys(builders.FAUX), 3);
        const vrais = idsV.map(id => builders.VRAIS[id]());
        const faux  = idsF.map(id => builders.FAUX[id]());
        const mixed = shuffle([...vrais, ...faux]);

        const lignes = mixed.map(a => a.texte);
        const bonnesReponses = mixed.map(a => a.verite);

        if (new Set(lignes).size === lignes.length) {
            // Texte OBLIGATOIRE (le serializer refuse l'énoncé vide au POST)
            const texte = '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>';
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'caracterisation_point_vf';
            return payload;
        }
        tries++;
    }

    // Fallback déterministe
    const builders = buildAffirmationsVF();
    const vrais = ['V1', 'V2', 'V3'].map(id => builders.VRAIS[id]());
    const faux  = ['F1', 'F3', 'F5'].map(id => builders.FAUX[id]());
    const mixed = shuffle([...vrais, ...faux]);
    const lignes = mixed.map(a => a.texte);
    const bonnesReponses = mixed.map(a => a.verite);
    const texte = '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>';
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'caracterisation_point_vf';
    return payload;
};

// ─── Format 5 — Texte à trous ────────────────────────────────

const genTexteTrou = () => {
    const [L1, L2, L3] = pickLetters(3);

    // On tire parmi les 3 arrangements possibles de 2 médiatrices citées.
    const arrangements = _arrangementsCotes(L1, L2, L3);
    const { cites, reste } = randomChoice(arrangements);
    const [X1, X2] = cites[0];
    const [Y1, Y2] = cites[1];
    const [Z1, Z2] = reste;

    // Phrase à compléter (syntaxe makeTexteTrou : {{mot}} = trou).
    const phrase =
        `Soit O le point d'intersection des {{médiatrices}} de [${X1}${X2}] et [${Y1}${Y2}]. ` +
        `Comme O appartient à la {{médiatrice}} de [${X1}${X2}], on a O${X1} = {{O${X2}}}. ` +
        `De même, comme O appartient à la médiatrice de [${Y1}${Y2}], on a O${Y1} = {{O${Y2}}}. ` +
        `Par conséquent, O${Z1} = O${Z2}, donc O appartient aussi à la médiatrice de [${Z1}${Z2}]. ` +
        `Les {{trois}} médiatrices sont donc concourantes en O.`;

    // Filtre indispensable : les extrémités du côté restant appartiennent aussi
    // aux côtés cités (chaque sommet est sur 2 côtés) → O${Z1}/O${Z2} égalent
    // très souvent un des mots attendus O${X2}/O${Y2}
    const distracteurs = filtrerDistracteurs(phrase, [
        'bissectrices', 'bissectrice',
        'hauteurs', 'médianes',
        'deux', 'quatre',
        `O${Z1}`, `O${Z2}`,
    ]);

    const texte = `<p>Complète le raisonnement suivant qui prouve que les trois médiatrices d'un triangle ${L1}${L2}${L3} sont concourantes.</p>`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_TROU), mode: 'drag', distracteurs });
    payload._meta.generatorKey = 'raisonnement_texte_trou';
    return payload;
};

// ─── Format 6 — Association hypothèse ↔ conclusion ────────────

const genAssociationRelier = () => {
    const [L1, L2, L3] = pickLetters(3);

    // 4 paires structurellement fixes, paramétrées par les 3 lettres.
    const pairs = [
        { source: `O appartient à la médiatrice de [${L1}${L2}]`, target: `O${L1} = O${L2}` },
        { source: `O appartient à la médiatrice de [${L2}${L3}]`, target: `O${L2} = O${L3}` },
        { source: `O${L1} = O${L2} et O${L2} = O${L3}`,           target: 'O appartient aux trois médiatrices' },
        { source: `O${L1} = O${L3}`,                              target: `O appartient à la médiatrice de [${L1}${L3}]` },
    ];

    const texte = `<p>Dans un triangle ${L1}${L2}${L3}, O est le point de concours des médiatrices. Associe chaque hypothèse à la conclusion qui en découle.</p>`;

    const payload = makeRelier(pairs, texte, opts(L_RELIER));
    payload._meta.generatorKey = 'association_hypothese_conclusion_relier';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    propriete_concours_qcm:                    genProprieteConcours,
    raisonnement_concours_ordonner:            genRaisonnementOrdonner,
    deduction_distances_qcm:                   genDeductionDistances,
    caracterisation_point_vf:                  genCaracterisationVF,
    raisonnement_texte_trou:                   genTexteTrou,
    association_hypothese_conclusion_relier:   genAssociationRelier,
};

export const CATEGORIES = [
    {
        id: 'connaissance_concours_mediatrices',
        label: 'Connaître le concours des médiatrices',
        formatsList: [
            { key: 'propriete_concours_qcm',   label: 'QCM' },
            { key: 'caracterisation_point_vf', label: 'V/F' },
        ],
    },
    {
        id: 'raisonnement_concours_mediatrices',
        label: 'Démontrer le concours des médiatrices',
        formatsList: [
            { key: 'raisonnement_concours_ordonner',          label: 'Ordonner' },
            { key: 'raisonnement_texte_trou',                 label: 'Trous' },
            { key: 'association_hypothese_conclusion_relier', label: 'Relier' },
        ],
    },
    {
        id: 'application_concours_mediatrices',
        label: 'Appliquer le concours (OA = OB = OC)',
        formatsList: [
            { key: 'deduction_distances_qcm', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

// Exposé pour tests unitaires (non utilisé en production).
export const _internals = {
    LETTRES,
    DISTANCES_POOL,
    pickLetters,
    _arrangementsCotes,
    buildAffirmationsVF,
    genProprieteConcours,
    genRaisonnementOrdonner,
    genDeductionDistances,
    genCaracterisationVF,
    genTexteTrou,
    genAssociationRelier,
};
